import { useEffect, useState } from 'react';
import { cn } from '../lib/util';
import { ImageOverlay } from '../components/ui/imageOverlay';
import { ImageModal } from '../components/imageModal';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { getAllGalleryEntries } from '../services/api';

const ITEMS_PER_PAGE = 12;

interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  position: string;
  text: string;
  category: string;
  images: string[];
}

export default function Gallery() {
  const [hoveredImage, setHoveredImage] = useState<GalleryImage | null>(null);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [mobileExpandedView, setMobileExpandedView] = useState<GalleryImage | null>(null);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch gallery data from the API
  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        const response = await getAllGalleryEntries() as any;
        const data = response.data.data;

        const mappedData = data.map((entry: any, index: number) => ({
          id: entry.id,
          src: entry.url,
          alt: entry.caption || entry.formatted_date,
          position: ['top-left', 'top-right', 'center', 'bottom-left', 'bottom-right'][index % 5],
          text: entry.caption || entry.formatted_date,
          category: entry.formatted_date,
          images: [],
        }));

        setGalleryImages(mappedData);
      } catch (error) {
        console.error('Failed to fetch gallery data:', error);
      }
    };

    fetchGalleryData();
  }, []);

  const totalPages = Math.ceil(galleryImages.length / ITEMS_PER_PAGE);
  const paginatedImages = galleryImages.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleImageClick = (image: GalleryImage) => {
    if (window.innerWidth <= 1024) {
      setMobileExpandedView(image);
    } else {
      setSelectedImage(image);
    }
  };

  if (mobileExpandedView) {
    return (
      <div className="min-h-screen bg-[#FBEFEF] p-4 mt-16">
        <button 
          onClick={() => setMobileExpandedView(null)}
          className="flex items-center gap-2 mb-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <div className="grid gap-4 md:grid-cols-2 pt-6">
          {[mobileExpandedView.src, ...mobileExpandedView.images].map((img, index) => (
            <div 
              key={index} 
              className="aspect-square w-full overflow-hidden rounded-lg shadow-lg"
            >
              <img
                src={img}
                alt={`${mobileExpandedView.text} gallery- ${index + 1}`}
                className="h-full w-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-12 mt-[4.5rem] lg:bg-[#FBEFEF]">
      <div className="bg-[#FBEFEF] lg:w-full flex items-center justify-center px-auto py-12">
        <img src="/images/gallery.svg" alt="gallery" className='w-[110px] lg:w-[160px]' />
      </div>
      
      {/* Desktop Layout */}
      <div className="hidden lg:block">
        <div className="relative grid grid-cols-3 grid-rows-3">
          {Array.from({ length: 9 }, (_, index) => {
            const row = Math.floor(index / 3);
            const col = index % 3;
            const image = galleryImages.find(img => {
              switch (img.position) {
                case 'top-left': return row === 0 && col === 0;
                case 'top-right': return row === 0 && col === 2;
                case 'center': return row === 1 && col === 1;
                case 'bottom-left': return row === 2 && col === 0;
                case 'bottom-right': return row === 2 && col === 2;
                default: return false;
              }
            });

            return (
              <div 
                key={index}
                className={cn(
                  "relative aspect-square w-full overflow-hidden z-10 group",
                  image ? "bg-transparent" : "bg-transparent"
                )}
                onMouseEnter={() => image && setHoveredImage(image)}
                onMouseLeave={() => setHoveredImage(null)}
                onClick={() => image && handleImageClick(image)}
              >
                {image && (
                  <>
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:opacity-0 cursor-pointer"
                    />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer">
                      <ImageOverlay text={image.text} />
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {hoveredImage && (
            <div 
              className="absolute left-0 top-0 z-0 h-full w-full transition-opacity duration-500 ease-in-out"
              style={{
                pointerEvents: 'none',
                opacity: hoveredImage ? 1 : 0,
              }}
            >
              <div className="relative h-full w-full">
                <img
                  src={hoveredImage.src}
                  alt={hoveredImage.alt}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile and Tablet Layout */}
      <div className="lg:hidden px-4 mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {paginatedImages.map((image) => (
            <div
              key={image.id}
              className="flex flex-col gap-2"
              onClick={() => handleImageClick(image)}
            >
              <div className="aspect-square w-full overflow-hidden rounded-lg shadow-md">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                />
              </div>
              <h2 className="text-center text-[12px] font-[600]">
                {image.text}
              </h2>
              <h2 className="text-center text-[12px] font-[600] mt-[-10px]">
                {image.category}
              </h2>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-full border border-gray-300 disabled:opacity-40 hover:border-[#B87775] transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-500">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-full border border-gray-300 disabled:opacity-40 hover:border-[#B87775] transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <ImageModal 
        images={selectedImage ? [selectedImage.src, ...selectedImage.images] : []} // Include the main media_url first
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
      />
    </div>
  );
}